import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiSearch, FiClock, FiX } from 'react-icons/fi';
import { CATEGORIAS, metaCategoria } from '../servicios/categorias';

/**
 * Selector de servicios del cliente: busca, agrupa por categoría y permite
 * quedarse solo con lo ya seleccionado cuando el catálogo se hace largo.
 */
const AsignadorServicios = ({ catalogo, seleccionados, onAlternar }) => {
  const [busqueda, setBusqueda] = useState('');
  const [soloSeleccionados, setSoloSeleccionados] = useState(false);

  const marcados = useMemo(() => new Set(seleccionados), [seleccionados]);

  const grupos = useMemo(() => {
    const texto = busqueda.trim().toLowerCase();

    const filtrados = catalogo.filter((servicio) => {
      if (soloSeleccionados && !marcados.has(servicio.id)) return false;
      if (!texto) return true;
      return servicio.titulo.toLowerCase().includes(texto)
        || metaCategoria(servicio.categoria).label.toLowerCase().includes(texto);
    });

    // Orden fijo de categorías para que la lista no baile entre búsquedas
    return CATEGORIAS
      .map((categoria) => ({
        categoria,
        ...metaCategoria(categoria),
        servicios: filtrados.filter((s) => s.categoria === categoria),
      }))
      .filter((grupo) => grupo.servicios.length > 0);
  }, [catalogo, busqueda, soloSeleccionados, marcados]);

  const totalHoras = useMemo(() => (
    catalogo.filter((s) => marcados.has(s.id)).reduce((acc, s) => acc + (s.horas || 0), 0)
  ), [catalogo, marcados]);

  const hayResultados = grupos.length > 0;

  if (catalogo.length === 0) {
    return (
      <p className="srv-asignador-vacio">
        Todavía no hay servicios en el catálogo.{' '}
        <Link to="/dashboard/gestion-servicios">Crear el primero</Link>.
      </p>
    );
  }

  return (
    <div className="srv-asignador">
      <div className="srv-asignador-barra">
        <FiSearch />
        <input
          type="text"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          placeholder="Buscar servicio por título o categoría..."
          aria-label="Buscar servicio"
        />
        {busqueda && (
          <button
            type="button"
            className="srv-asignador-limpiar"
            onClick={() => setBusqueda('')}
            title="Limpiar búsqueda"
            aria-label="Limpiar búsqueda"
          >
            <FiX />
          </button>
        )}
      </div>

      <div className="srv-asignador-resumen">
        <button
          type="button"
          className={`srv-asignador-filtro ${soloSeleccionados ? 'activo' : ''}`}
          onClick={() => setSoloSeleccionados((v) => !v)}
          aria-pressed={soloSeleccionados}
        >
          Solo seleccionados
        </button>
        <span className="srv-asignador-contador">
          {seleccionados.length} de {catalogo.length}
          {totalHoras > 0 && <> · <FiClock /> {totalHoras} h</>}
        </span>
      </div>

      <div className="srv-asignador-lista">
        {!hayResultados && (
          <p className="srv-asignador-vacio">
            {soloSeleccionados ? 'Todavía no has seleccionado ningún servicio.' : 'Ningún servicio coincide con la búsqueda.'}
          </p>
        )}

        {grupos.map((grupo) => {
          const IconoCat = grupo.icon;
          return (
            <div key={grupo.categoria} className="srv-asignador-grupo">
              <p className="srv-asignador-grupo-titulo">
                <IconoCat /> {grupo.label}
                <span>{grupo.servicios.length}</span>
              </p>

              {grupo.servicios.map((servicio) => {
                const marcado = marcados.has(servicio.id);
                return (
                  <label
                    key={servicio.id}
                    className={`srv-asignador-item ${marcado ? 'marcado' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={marcado}
                      onChange={() => onAlternar(servicio.id)}
                    />
                    <span className="srv-asignador-info">
                      <span className="srv-asignador-titulo">{servicio.titulo}</span>
                      {servicio.descripcion?.length > 0 && (
                        <span className="srv-asignador-desc">
                          {servicio.descripcion.join(' · ')}
                        </span>
                      )}
                    </span>
                    {servicio.horas !== null && typeof servicio.horas !== 'undefined' && (
                      <span className="srv-horas"><FiClock /> {servicio.horas} h</span>
                    )}
                  </label>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AsignadorServicios;
